
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import CartIcon from './icons/CartIcon';
import XIcon from './icons/XIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ChevronUpIcon from './icons/ChevronUpIcon';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import StaticWeekBar from './StaticWeekBar';
import ChefHatIcon from './icons/ChefHatIcon';
import BeefIcon from './icons/BeefIcon';
import LeafIcon from './icons/LeafIcon';
import UtensilsIcon from './icons/UtensilsIcon';
import BeakerIcon from './icons/BeakerIcon';
import ShoppingBagIcon from './icons/ShoppingBagIcon';
import QuantityCapsule from './QuantityCapsule';
import type { Language, ActiveShoppingCart, MealPlan, Recipe, DayOfWeek, PlannedMeal, ShoppingCategory } from '../types';
import { getTranslation, isRTL } from '../utils/translations';
import { DAYS_OF_WEEK } from '../types';
import { getCanonicalName, normalizeQuantity, formatConsolidatedQuantity, detectCategory, unifyUnit, isOrdinaryShoppingItem } from '../utils/unitConversion';

// Define CheckIcon locally
const CheckIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
);

interface BulkShoppingListModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack?: () => void;
    mealPlan: Record<string, MealPlan> | MealPlan;
    recipes: Recipe[];
    orderedDays: DayOfWeek[];
    language?: Language;
    activeCart?: ActiveShoppingCart | null;
    onUpdateActiveCart?: (cart: ActiveShoppingCart) => Promise<void>;
    weeks?: Date[][]; 
    onChangeWeeks?: () => void; 
    showWeekBar?: boolean;
    categoryOverrides?: Record<string, ShoppingCategory>;
}

const formatDateLocal = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// Category Configuration mapping for the Bulk Modal (similar to SmartShoppingList)
const CATEGORY_CONFIG: Record<ShoppingCategory, { icon: React.ReactNode, color: string, bg: string }> = {
    'Meat': { icon: <BeefIcon className="w-4 h-4" />, color: "text-rose-600 dark:text-rose-400", bg: "bg-rose-50/50 dark:bg-rose-900/20" },
    'Vegetables': { icon: <LeafIcon className="w-4 h-4" />, color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50/50 dark:bg-emerald-900/20" },
    'Fruits': { icon: <div className="text-lg">🍎</div>, color: "text-red-500 dark:text-red-400", bg: "bg-red-50/30 dark:bg-red-900/20" },
    'Dairy': { icon: <div className="text-lg">🥛</div>, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-50/50 dark:bg-blue-900/20" },
    'Spices': { icon: <div className="text-lg">🌶️</div>, color: "text-orange-700 dark:text-orange-400", bg: "bg-orange-50/40 dark:bg-orange-900/20" },
    'Snacks': { icon: <div className="text-lg">🍿</div>, color: "text-yellow-700 dark:text-yellow-400", bg: "bg-yellow-50/50 dark:bg-yellow-900/20" },
    'BabyProducts': { icon: <div className="text-lg">🍼</div>, color: "text-pink-600 dark:text-pink-400", bg: "bg-pink-50/50 dark:bg-pink-900/20" },
    'Canned': { icon: <div className="text-lg">🥫</div>, color: "text-orange-600 dark:text-orange-400", bg: "bg-orange-50/50 dark:bg-orange-900/20" },
    'Pantry': { icon: <div className="text-lg">🥖</div>, color: "text-amber-800 dark:text-amber-300", bg: "bg-orange-100/30 dark:bg-amber-900/20" },
    'Cleaning': { icon: <BeakerIcon className="w-4 h-4" />, color: "text-purple-600 dark:text-purple-400", bg: "bg-purple-50/50 dark:bg-purple-900/20" },
    'Toiletries': { icon: <div className="text-lg">🪥</div>, color: "text-pink-500 dark:text-pink-400", bg: "bg-pink-50/50 dark:bg-pink-900/20" },
    'GeneralFood': { icon: <UtensilsIcon className="w-4 h-4" />, color: "text-indigo-600 dark:text-indigo-400", bg: "bg-indigo-50/50 dark:bg-indigo-900/20" },
    'Frozen': { icon: <div className="text-lg">❄️</div>, color: "text-sky-600 dark:text-sky-400", bg: "bg-sky-50/50 dark:bg-sky-900/20" },
    'Bakery': { icon: <div className="text-lg">🥐</div>, color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50/50 dark:bg-amber-900/20" },
    'Beverages': { icon: <div className="text-lg">🥤</div>, color: "text-teal-600 dark:text-teal-400", bg: "bg-teal-50/50 dark:bg-teal-900/20" },
    'Other': { icon: <ShoppingBagIcon className="w-4 h-4" />, color: "text-slate-500 dark:text-slate-400", bg: "bg-slate-50 dark:bg-slate-800" },
    'General': { icon: <CartIcon className="w-4 h-4" />, color: "text-slate-600 dark:text-slate-400", bg: "bg-slate-50 dark:bg-slate-800" }
};

const ORDERED_CATEGORIES: ShoppingCategory[] = ['Meat', 'Fruits', 'Vegetables', 'Dairy', 'Spices', 'Snacks', 'BabyProducts', 'Frozen', 'Bakery', 'Canned', 'Pantry', 'GeneralFood', 'Beverages', 'Cleaning', 'Toiletries', 'General', 'Other'];

// Sub-component for the Recipe Card
const RecipeGroupCard: React.FC<{
    recipe: Recipe;
    instances: { pm: PlannedMeal; day: string; date: Date }[];
    servingsOverrides: Record<string, number>;
    onUpdateServings: (updates: { pmId: string; count: number }[]) => void;
    t: (key: string) => string;
    baseFontSize: number;
    language?: Language;
}> = ({ recipe, instances, servingsOverrides, onUpdateServings, t, baseFontSize, language }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    // Calculate total servings for this recipe across all instances
    const currentTotalServings = instances.reduce((sum, inst) => {
        const override = servingsOverrides[inst.pm.id];
        return sum + (override !== undefined ? override : inst.pm.plannedServings);
    }, 0);

    const activeIngredients = recipe.ingredients.filter(i => i.includedInShoppingList !== false);
    const ingredientCount = activeIngredients.length;

    const handleTotalUpdate = (newTotal: number) => {
        if (instances.length === 0) return;
        
        if (instances.length === 1) {
            onUpdateServings([{ pmId: instances[0].pm.id, count: newTotal }]);
            return;
        }

        const ratio = newTotal / currentTotalServings;
        const updates = instances.map(inst => {
            const current = servingsOverrides[inst.pm.id] !== undefined ? servingsOverrides[inst.pm.id] : inst.pm.plannedServings;
            let newCount = Math.round(current * ratio * 2) / 2;
            if (newCount < 0.5 && newTotal > 0) newCount = 0.5;
            return { pmId: inst.pm.id, count: newCount };
        });
        
        const calculatedSum = updates.reduce((s, u) => s + u.count, 0);
        const diff = newTotal - calculatedSum;
        if (diff !== 0) {
            updates[updates.length - 1].count += diff;
        }
        
        onUpdateServings(updates);
    };

    return (
        <div className="bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-xl mb-3 shadow-sm overflow-hidden animate-fade-in transition-all">
            <div 
                className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${isExpanded ? 'bg-white dark:bg-slate-900' : 'bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-3 overflow-hidden min-w-0 flex-grow">
                    <div className="text-gray-400 flex-shrink-0 transition-transform duration-200">
                        {isExpanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
                    </div>
                    
                    <div className="p-1.5 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-lg shadow-sm text-brand-sage flex-shrink-0">
                        <ChefHatIcon className="w-4 h-4" />
                    </div>
                    
                    <div className="flex items-baseline gap-2 overflow-hidden">
                        <h4 
                            className="font-bold text-slate-800 dark:text-slate-200 truncate"
                            style={{ fontSize: baseFontSize + 'px' }}
                        >
                            {recipe.name}
                        </h4>
                        
                        {!isExpanded && (
                            <span className="text-xs text-gray-400 dark:text-slate-500 font-normal whitespace-nowrap flex-shrink-0">
                                ({ingredientCount} {t('ingredients').toLowerCase()})
                            </span>
                        )}
                    </div>
                </div>
                
                <div 
                    className="flex items-center gap-2 flex-shrink-0 ml-2 rtl:mr-2 rtl:ml-0"
                    onClick={(e) => e.stopPropagation()}
                >
                    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest hidden sm:inline">{t('servings')}</span>
                    <QuantityCapsule 
                        value={currentTotalServings} 
                        unit="servings" 
                        onUpdate={(v) => handleTotalUpdate(v)}
                        language={language}
                        bgClass="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm"
                        hideUnitDisplay={true}
                    />
                </div>
            </div>

            {isExpanded && (
                <div className="p-3 space-y-1 animate-fade-in bg-slate-50/50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800">
                    {activeIngredients.map((ing, idx) => {
                        if (isOrdinaryShoppingItem(ing.name)) return null;
                        const canonicalName = getCanonicalName(ing.name);

                        const totalQty = instances.reduce((sum, inst) => {
                            const s = servingsOverrides[inst.pm.id] !== undefined ? servingsOverrides[inst.pm.id] : inst.pm.plannedServings;
                            return sum + (ing.quantity * (s / recipe.servings));
                        }, 0);
                        
                        const { normalizedQty } = normalizeQuantity(totalQty, ing.unit, ing.name);
                        
                        // Force base unit for display in this view to match logic
                        const unifiedUnit = unifyUnit(ing.unit);
                        let displayUnit = ing.unit;
                        if (unifiedUnit === 'kg' || unifiedUnit === 'g') displayUnit = 'g';
                        else if (['liter', 'ml', 'cup', 'tbsp', 'tsp'].includes(unifiedUnit)) displayUnit = 'ml';

                        const finalQty = normalizedQty > 0 ? normalizedQty : totalQty;
                        const displayQty = formatConsolidatedQuantity(finalQty, displayUnit, t, canonicalName);

                        return (
                            <div key={idx} className="flex items-start text-xs text-slate-500 dark:text-slate-400 py-0.5 px-2">
                                <span className="text-slate-300 dark:text-slate-600 mr-2 mt-[2px]">•</span>
                                <span className="flex-grow">
                                    <span className="font-bold text-slate-600 dark:text-slate-300">{displayQty}</span> 
                                    <span className={`mx-1 ${ing.isMeat ? 'text-rose-700 dark:text-rose-400 font-medium' : 'text-slate-500 dark:text-slate-400'}`}>{ing.name}</span>
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const BulkShoppingListModal: React.FC<BulkShoppingListModalProps> = ({ 
    isOpen, onClose, onBack, mealPlan, recipes, orderedDays, language, activeCart, onUpdateActiveCart, weeks, onChangeWeeks, showWeekBar = true, categoryOverrides = {}
}) => {
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);
    
    const [viewMode, setViewMode] = useState<'ingredients' | 'recipes'>('ingredients');
    const [copyButtonText, setCopyButtonText] = useState(t('copyList'));
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    
    const [filterDays, setFilterDays] = useState<Set<DayOfWeek>>(new Set([...DAYS_OF_WEEK]));
    const [baseFontSize, setBaseFontSize] = useState(14);
    const [servingsOverrides, setServingsOverrides] = useState<Record<string, number>>({});
    
    const [collapsedCategories, setCollapsedCategories] = useState<Set<ShoppingCategory>>(() => new Set(ORDERED_CATEGORIES));

    useEffect(() => {
        if (isOpen) {
            setCollapsedCategories(new Set(ORDERED_CATEGORIES));
        }
    }, [isOpen]);

    const datesToInclude = useMemo(() => {
        const dates: Date[] = [];
        if (weeks && Array.isArray(weeks)) {
            weeks.forEach((weekDates) => {
                if (weekDates) dates.push(...weekDates);
            });
        }
        return dates;
    }, [weeks]);

    // Unique set of date strings (YYYY-MM-DD) involved in this bulk sync
    // This is used to "clear" old items from the Active List for these dates before adding new ones
    const datesStringsToSync = useMemo(() => {
        const set = new Set<string>();
        datesToInclude.forEach(d => {
            const dayName = DAYS_OF_WEEK[d.getDay()];
            if (showWeekBar && !filterDays.has(dayName)) return;
            set.add(formatDateLocal(d));
        });
        return set;
    }, [datesToInclude, filterDays, showWeekBar]);

    const { shoppingList, recipeList, excludedOrdinaryItems } = useMemo(() => {
        const list: Record<string, { 
            name: string;
            totalNormalized: number; 
            unit: string; 
            isMeat: boolean;
            category: ShoppingCategory;
            sources: { recipeName: string; quantity: number; unit: string; day?: string; rawDay?: DayOfWeek; servings?: number; originId?: string; originType?: 'day' | 'recipe' | 'list' }[];
        }> = {};

        const excludedOrdinary = new Set<string>();

        const recipesMap = new Map<string, { recipe: Recipe, instances: { pm: PlannedMeal, day: string, date: Date }[] }>();
        
        datesToInclude.forEach(date => {
            const dayName = DAYS_OF_WEEK[date.getDay()];
            if (showWeekBar && !filterDays.has(dayName)) return;

            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay());
            const weekKey = formatDateLocal(weekStart);
            const dateKey = formatDateLocal(date);
            
            const planCollection = mealPlan as any;
            const plan = (planCollection[weekKey] ? planCollection[weekKey][dayName] : planCollection[dayName]) || [];
            
            plan.forEach((pm: any) => {
                if (pm.sourcingStatus && pm.sourcingStatus !== 'buy') return;

                const recipe = recipes.find(r => r.id === pm.recipeId);
                if (!recipe || recipe.isDeleted) return;

                const effectiveServings = servingsOverrides[pm.id] !== undefined ? servingsOverrides[pm.id] : pm.plannedServings;
                const factor = effectiveServings / recipe.servings;

                if (!recipesMap.has(recipe.id)) {
                    recipesMap.set(recipe.id, { recipe, instances: [] });
                }
                recipesMap.get(recipe.id)!.instances.push({ pm, day: t('day_' + dayName), date });

                recipe.ingredients.forEach(ing => {
                    if (ing.includedInShoppingList === false) return;
                    
                    if (isOrdinaryShoppingItem(ing.name)) {
                        excludedOrdinary.add(ing.name.trim());
                        return;
                    }

                    const canonicalName = getCanonicalName(ing.name);

                    const { normalizedQty } = normalizeQuantity(ing.quantity * factor, ing.unit, canonicalName);
                    const category = categoryOverrides[ing.name.toLowerCase().trim()] || detectCategory(ing.name, ing.isMeat);
                    const key = canonicalName;

                    // Correctly handle unit assignment for normalized quantities
                    // If normalized (e.g. converted kg to g), we must use 'g' as unit
                    const unifiedUnit = unifyUnit(ing.unit);
                    let storageUnit = ing.unit;
                    if (unifiedUnit === 'kg' || unifiedUnit === 'g') {
                        storageUnit = 'g';
                    } else if (['liter', 'ml', 'cup', 'tbsp', 'tsp'].includes(unifiedUnit)) {
                        storageUnit = 'ml';
                    } else {
                        // For non-normalized units like 'unit', 'pinch', etc. keep as is
                        storageUnit = unifiedUnit;
                    }

                    const sourceData = { 
                        recipeName: recipe.name, 
                        quantity: ing.quantity * factor, 
                        unit: ing.unit, 
                        servings: effectiveServings, 
                        day: t('day_' + dayName), 
                        rawDay: dayName,
                        originId: dateKey, // Use specific date string (YYYY-MM-DD) as the ID for idempotency
                        originType: 'day' as const 
                    };

                    if (list[key]) {
                        list[key].totalNormalized += normalizedQty;
                        list[key].sources.push(sourceData);
                        if (ing.isMeat) list[key].isMeat = true;
                    } else {
                        list[key] = { 
                            name: canonicalName, 
                            totalNormalized: normalizedQty, 
                            unit: storageUnit, 
                            isMeat: ing.isMeat, 
                            category: category,
                            sources: [sourceData] 
                        };
                    }
                });
            });
        });

        const recipesArray = Array.from(recipesMap.values()).sort((a, b) => a.recipe.name.localeCompare(b.recipe.name));

        return {
            shoppingList: list,
            recipeList: recipesArray,
            excludedOrdinaryItems: [...excludedOrdinary].sort(),
        };
    }, [mealPlan, recipes, datesToInclude, language, filterDays, showWeekBar, servingsOverrides, categoryOverrides, t]);

    useEffect(() => {
        if (selectedItems.size === 0 && Object.keys(shoppingList).length > 0) {
            setSelectedItems(new Set(Object.keys(shoppingList)));
        }
    }, [shoppingList]);

    if (!isOpen) return null;

    const entries = Object.entries(shoppingList) as [string, typeof shoppingList[string]][];

    const groupedEntries = useMemo(() => {
        const groups: Record<ShoppingCategory, [string, typeof shoppingList[string]][]> = {} as any;
        entries.forEach(([key, data]) => {
            const cat = data.category || 'General';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push([key, data]);
        });
        
        Object.keys(groups).forEach(cat => {
            groups[cat as ShoppingCategory].sort((a, b) => a[0].localeCompare(b[0]));
        });
        
        return groups;
    }, [entries]);

    const handleToggleFilterDay = (day: DayOfWeek) => {
        setFilterDays(prev => {
            const next = new Set(prev);
            if (next.has(day)) next.delete(day); else next.add(day);
            return next;
        });
    };

    const handleSetFilterDays = (days: DayOfWeek[]) => {
        setFilterDays(new Set(days));
    };

    const handleUpdateServings = (updates: { pmId: string, count: number }[]) => {
        setServingsOverrides(prev => {
            const next = { ...prev };
            updates.forEach(u => next[u.pmId] = u.count);
            return next;
        });
    };

    const toggleCategorySelection = (categoryItems: [string, any][]) => {
        const itemKeys = categoryItems.map(([key]) => key);
        const allSelected = itemKeys.every(key => selectedItems.has(key));
        
        setSelectedItems(prev => {
            const next = new Set(prev);
            if (allSelected) {
                // Deselect all
                itemKeys.forEach(key => next.delete(key));
            } else {
                // Select all
                itemKeys.forEach(key => next.add(key));
            }
            return next;
        });
    };

    const getDisplayName = (canonicalName: string) => {
        const key = 'ingredient_' + canonicalName.toLowerCase();
        const translated = t(key);
        return translated !== key ? translated : canonicalName;
    };

    const handleCopyToActiveCart = async () => {
        if (!onUpdateActiveCart || !activeCart) return;
        setIsAddingToCart(true);
        try {
            // STEP 1: CLEANUP
            // Remove existing items/sources in the active cart that belong to the dates we are syncing
            // This ensures idempotency: re-syncing a day replaces its old content
            
            // Remember which item IDs were marked as done in the active cart
            const doneItemIds = new Set(activeCart.items.filter(i => i.done).map(i => i.id));

            // Map existing items, filtering out sources from the dates we are currently syncing
            const cleanedItems = activeCart.items.map(item => {
                const newSources = item.sources.filter(source => {
                    // Keep the source if it's NOT from one of the dates we are updating
                    // OR if it's a manual/list/recipe add (originType != 'day')
                    // Note: source.originId for 'day' type holds the date string (YYYY-MM-DD)
                    if (source.originType === 'day' && source.originId && datesStringsToSync.has(source.originId)) {
                        return false; // Remove this source, we will re-add if selected
                    }
                    return true;
                });
                return { ...item, sources: newSources };
            }).filter(item => {
                // Keep item if it still has sources
                return item.sources.length > 0;
            });

            // STEP 2: ADD NEW
            // Merge the selected items from the bulk calculation into the cleaned list
            const nextItems = [...cleanedItems];

            entries.forEach(([key, data]) => {
                if (!selectedItems.has(key)) return;
                
                const localizedName = getDisplayName(data.name);
                const itemId = `${key.toLowerCase().trim()}_consolidated`;
                const existingIndex = nextItems.findIndex(i => i.id === itemId);
                
                const newSources = data.sources.map((s, idx) => {
                    // Normalize source quantity to match the parent consolidated unit
                    // This is CRITICAL to fix the "800 kg vs 800 g" mismatch.
                    // If parent item unit is 'g', but source was from 'kg', we must convert source quantity to 'g'.
                    let qtyToSave = s.quantity;
                    const unifiedListUnit = unifyUnit(data.unit);
                    
                    // If parent is normalized to base units (g or ml), normalize the source too
                    if (['g', 'ml'].includes(unifiedListUnit)) {
                        const { normalizedQty } = normalizeQuantity(s.quantity, s.unit);
                        qtyToSave = normalizedQty;
                    }

                    return {
                        id: `${Date.now()}-${idx}-${Math.random()}`, 
                        originName: s.recipeName, 
                        originId: s.originId, // This is the date string YYYY-MM-DD
                        originType: s.originType || 'day', 
                        quantity: qtyToSave 
                    };
                });

                if (existingIndex > -1) {
                    nextItems[existingIndex] = { 
                        ...nextItems[existingIndex], 
                        done: doneItemIds.has(itemId), 
                        sources: [...nextItems[existingIndex].sources, ...newSources] 
                    };
                } else {
                    nextItems.push({ 
                        id: itemId, 
                        name: localizedName, 
                        unit: data.unit, 
                        isMeat: data.isMeat, 
                        category: data.category,
                        done: doneItemIds.has(itemId), 
                        sources: newSources 
                    });
                }
            });
            await onUpdateActiveCart({ ...activeCart, items: nextItems });
            onClose();
        } finally {
            setIsAddingToCart(false);
        }
    };

    const handleCopyText = () => {
        const itemsToCopy = entries.filter(([key]) => selectedItems.has(key));
        if (itemsToCopy.length === 0) {
            setCopyButtonText(t('nothingSelected'));
            setTimeout(() => setCopyButtonText(t('copyList')), 2000);
            return;
        }
        const text = itemsToCopy.map(([key, data]) => {
             const displayName = getDisplayName(data.name);
             const displayQty = formatConsolidatedQuantity(data.totalNormalized, data.unit, t, data.name);
             return `${displayQty} ${displayName}`.trim();
        }).join('\n');
        navigator.clipboard.writeText(text).then(() => {
            setCopyButtonText(t('copied'));
            setTimeout(() => setCopyButtonText(t('copyList')), 2000);
        });
    };

    const toggleCategory = (cat: ShoppingCategory) => {
        setCollapsedCategories(prev => {
            const next = new Set(prev);
            if (next.has(cat)) next.delete(cat); else next.add(cat);
            return next;
        });
    };

    const allCategoriesExpanded = ORDERED_CATEGORIES.every(cat => !collapsedCategories.has(cat));

    const toggleAllCategories = () => {
        if (allCategoriesExpanded) {
            setCollapsedCategories(new Set(ORDERED_CATEGORIES));
        } else {
            setCollapsedCategories(new Set());
        }
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[500] flex items-center justify-center p-4 animate-fade-in font-rubik">
            <div
                dir={rtl ? 'rtl' : 'ltr'}
                className="bg-white dark:bg-slate-900 w-full max-w-lg flex flex-col max-h-[min(90dvh,calc(100dvh-2rem))] overflow-hidden rounded-[2.5rem] shadow-2xl border border-white/10 dark:border-slate-800"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 py-4 border-b border-slate-50 dark:border-slate-800 flex justify-between items-center bg-white dark:bg-slate-900 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        {onBack && (
                            <button 
                                onClick={onBack}
                                className="p-2 -ms-2 text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-all active:scale-95 ltr:me-1 rtl:ms-1"
                            >
                                <ArrowLeftIcon className="w-6 h-6 rtl:rotate-180" />
                            </button>
                        )}
                        <div className="p-2.5 bg-sky-500 rounded-full text-white shadow-lg shadow-sky-100 dark:shadow-sky-900/50 flex items-center justify-center">
                            <CartIcon className="h-6 w-6" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">{t('bulkShoppingList')}</h2>
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{entries.length} {t('itemsLabel')}</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        <div className="flex items-center bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full h-8 px-1 hidden sm:flex" dir="ltr">
                            <button onClick={() => setBaseFontSize(Math.max(12, baseFontSize - 2))} className="w-7 h-full flex items-center justify-center text-slate-400 dark:text-slate-500 font-bold hover:text-slate-800 dark:hover:text-slate-200 transition-colors">-</button>
                            <div className="w-6 h-full flex items-center justify-center text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase select-none">A</div>
                            <button onClick={() => setBaseFontSize(Math.min(24, baseFontSize + 2))} className="w-7 h-full flex items-center justify-center text-slate-400 dark:text-slate-500 font-bold hover:text-slate-800 dark:hover:text-slate-200 transition-colors">+</button>
                        </div>

                        <button onClick={onClose} className="text-slate-300 hover:text-slate-800 dark:hover:text-slate-200 transition-colors p-2">
                            <XIcon className="h-7 w-7" />
                        </button>
                    </div>
                </div>

                {showWeekBar && (
                    <StaticWeekBar 
                        selectedDays={filterDays} 
                        onToggleDay={handleToggleFilterDay} 
                        onSetDays={handleSetFilterDays}
                        language={language}
                    />
                )}

                <div className="px-6 pb-4 bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800 pt-2 space-y-2">
                    <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                        <button 
                            onClick={() => setViewMode('ingredients')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${viewMode === 'ingredients' ? 'bg-white dark:bg-slate-700 shadow text-slate-800 dark:text-slate-100' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                        >
                            <span>🧾</span> {t('ingredients')}
                        </button>
                        <button 
                            onClick={() => setViewMode('recipes')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${viewMode === 'recipes' ? 'bg-white dark:bg-slate-700 shadow text-slate-800 dark:text-slate-100' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                        >
                            <span>🍳</span> {t('recipes')}
                        </button>
                    </div>
                    {viewMode === 'ingredients' && entries.length > 0 && (
                        <button
                            type="button"
                            onClick={toggleAllCategories}
                            className="block w-full text-start text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                        >
                            {allCategoriesExpanded ? t('collapseAll') : t('expandAll')}
                        </button>
                    )}
                </div>
                
                <div className={`flex-grow overflow-y-auto scrollbar-none ${viewMode === 'ingredients' ? 'bg-slate-50/50 dark:bg-slate-950 px-4 py-4 space-y-4' : 'bg-slate-50/50 dark:bg-slate-950 px-5 py-4 space-y-2'}`}>
                    {viewMode === 'ingredients' && excludedOrdinaryItems.length > 0 && (
                        <div
                            role="note"
                            className="flex items-start gap-3 p-3 rounded-2xl bg-sky-50/80 dark:bg-sky-950/30 border border-sky-100 dark:border-sky-800/50 animate-fade-in"
                        >
                            <div
                                className="w-5 h-5 rounded-full bg-sky-500/15 dark:bg-sky-400/20 flex items-center justify-center shrink-0 mt-0.5"
                                aria-hidden="true"
                            >
                                <span className="text-[10px] font-black text-sky-600 dark:text-sky-300 leading-none">i</span>
                            </div>
                            <div className="min-w-0">
                                <p className="text-[11px] leading-relaxed text-sky-900/90 dark:text-sky-100/90">
                                    {t('ordinaryItemsRemovedMessage')}
                                </p>
                                <p className="text-[10px] mt-1.5 font-semibold text-sky-700/80 dark:text-sky-300/70">
                                    {excludedOrdinaryItems.map((name) => getDisplayName(name)).join(' · ')}
                                </p>
                            </div>
                        </div>
                    )}
                    {viewMode === 'ingredients' ? (
                        entries.length > 0 ? (
                            ORDERED_CATEGORIES.map(cat => {
                                const groupItems = groupedEntries[cat];
                                if (!groupItems || groupItems.length === 0) return null;
                                
                                const isCollapsed = collapsedCategories.has(cat);
                                const config = CATEGORY_CONFIG[cat];
                                const allSelected = groupItems.every(([key]) => selectedItems.has(key));
                                const itemCount = groupItems.length;
                                
                                return (
                                    <div key={cat} className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-all duration-300">
                                        <div 
                                            onClick={() => toggleCategory(cat)}
                                            className={`flex items-center justify-between px-6 py-3 border-b border-slate-100 dark:border-slate-800 cursor-pointer hover:opacity-90 transition-all ${config.bg}`}
                                        >
                                            <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
                                                <button
                                                    onClick={() => toggleCategorySelection(groupItems)}
                                                    className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${allSelected ? 'bg-slate-800 border-slate-800 text-white dark:bg-slate-200 dark:border-slate-200 dark:text-slate-900' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-slate-400'}`}
                                                >
                                                    {allSelected && <CheckIcon className="w-3.5 h-3.5" />}
                                                </button>

                                                <div className={`${config.color} opacity-80`}>{config.icon}</div>
                                                <h4 className={`text-xs font-black uppercase tracking-widest ${config.color}`}>{t(cat.toLowerCase() as any)}</h4>
                                                
                                                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ml-1 bg-white/50 dark:bg-black/20 ${config.color}`}>
                                                    {itemCount}
                                                </span>
                                            </div>
                                            <div className="text-slate-300 dark:text-slate-600 transition-transform duration-300" style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}>
                                                <ChevronDownIcon className="w-4 h-4" />
                                            </div>
                                        </div>
                                        
                                        {!isCollapsed && (
                                            <div className="divide-y divide-slate-50 dark:divide-slate-800 bg-white dark:bg-slate-900 animate-fade-in">
                                                {groupItems.map(([key, data]) => {
                                                    const isSelected = selectedItems.has(key);
                                                    const isExpanded = expandedItems.has(key);
                                                    const displayQty = formatConsolidatedQuantity(data.totalNormalized, data.unit, t, data.name);
                                                    const displayName = getDisplayName(data.name);
                                                    
                                                    return (
                                                        <div key={key} className={`transition-colors ${isSelected ? 'bg-slate-50/60 dark:bg-slate-800/30' : ''}`}>
                                                            <div 
                                                                onClick={() => setSelectedItems(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; })} 
                                                                className="flex items-center gap-3 py-2 px-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors min-h-[3.5rem]"
                                                            >
                                                                <div className={`
                                                                    w-5 h-5 rounded-full border flex items-center justify-center transition-all flex-shrink-0
                                                                    ${isSelected 
                                                                        ? (data.isMeat ? 'bg-rose-500 border-rose-500' : 'bg-sky-500 border-sky-500') 
                                                                        : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'}
                                                                `}>
                                                                    {isSelected && (
                                                                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                    )}
                                                                </div>

                                                                <div className="flex-grow min-w-0 flex items-center justify-between gap-3">
                                                                    <span 
                                                                        className={`font-bold leading-tight truncate ${data.isMeat ? 'text-rose-900 dark:text-rose-400' : 'text-slate-800 dark:text-slate-200'} ${isSelected ? '' : 'opacity-80'}`}
                                                                        style={{ fontSize: baseFontSize + 'px' }}
                                                                    >
                                                                        {displayName}
                                                                    </span>
                                                                    <span className="text-[11px] font-mono font-black text-slate-400 dark:text-slate-500 whitespace-nowrap bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                                                                        {displayQty}
                                                                    </span>
                                                                </div>

                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); setExpandedItems(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; }); }} 
                                                                    className={`p-1.5 text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 transition-all rounded-full hover:bg-slate-200 dark:hover:bg-slate-800 ${isExpanded ? 'rotate-180 bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400' : ''}`}
                                                                >
                                                                    <ChevronDownIcon className="w-4 h-4" />
                                                                </button>
                                                            </div>

                                                            {isExpanded && (
                                                                <div className="bg-slate-50/50 dark:bg-slate-800/30 px-12 pb-3 pt-0 text-[10px] space-y-1.5 shadow-inner">
                                                                    <div className="h-px bg-slate-100 dark:bg-slate-700 mb-2"></div>
                                                                    {data.sources.map((s, idx) => {
                                                                        // Normalize source quantity to match the parent consolidated unit
                                                                        let qtyToFormat = s.quantity;
                                                                        const unifiedListUnit = unifyUnit(data.unit);
                                                                        
                                                                        // If parent is normalized to base units (g or ml), normalize the source too
                                                                        if (['g', 'ml'].includes(unifiedListUnit)) {
                                                                            const { normalizedQty } = normalizeQuantity(s.quantity, s.unit);
                                                                            qtyToFormat = normalizedQty;
                                                                        }

                                                                        const sourceQty = formatConsolidatedQuantity(qtyToFormat, data.unit, t);
                                                                        
                                                                        return (
                                                                            <div key={idx} className="flex justify-between items-center text-slate-500 dark:text-slate-400">
                                                                                <div className="flex items-center gap-1.5 overflow-hidden">
                                                                                    <span className="truncate font-medium">{s.recipeName}</span>
                                                                                    {s.day && <span className="text-slate-300 dark:text-slate-600">• {s.day}</span>}
                                                                                </div>
                                                                                <span className="font-mono whitespace-nowrap opacity-70 bg-white dark:bg-slate-900 px-1 rounded border border-slate-100 dark:border-slate-800">
                                                                                    {sourceQty}
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-24 opacity-20 flex flex-col items-center">
                                <CartIcon className="w-16 h-16 mb-4 text-slate-400 dark:text-slate-600" />
                                <p className="font-black text-xs uppercase tracking-[0.3em] text-center px-8 text-slate-400 dark:text-slate-600">
                                    {showWeekBar ? t('selectDaysHint') : t('noMonthlyItemsHint')}
                                </p>
                            </div>
                        )
                    ) : (
                        recipeList.length > 0 ? (
                            <div className="pb-4">
                                {recipeList.map(({ recipe, instances }) => (
                                    <RecipeGroupCard 
                                        key={recipe.id}
                                        recipe={recipe}
                                        instances={instances}
                                        servingsOverrides={servingsOverrides}
                                        onUpdateServings={handleUpdateServings}
                                        t={t}
                                        baseFontSize={baseFontSize}
                                        language={language}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-24 opacity-20 flex flex-col items-center">
                                <ChefHatIcon className="w-16 h-16 mb-4 text-slate-400 dark:text-slate-600" />
                                <p className="font-black text-xs uppercase tracking-[0.3em] text-center px-8 text-slate-400 dark:text-slate-600">
                                    {t('noResults')}
                                </p>
                            </div>
                        )
                    )}
                </div>

                {viewMode === 'ingredients' && (
                    <div className="px-6 py-5 border-t border-slate-50 dark:border-slate-800 bg-white dark:bg-slate-900 flex-shrink-0 shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.05)] pb-[max(1.25rem,env(safe-area-inset-bottom))]">
                        <div className="flex gap-2 w-full">
                            <button 
                                onClick={handleCopyToActiveCart} 
                                disabled={isAddingToCart || selectedItems.size === 0} 
                                className="flex-[2] py-3 bg-slate-900 dark:bg-slate-700 text-white rounded-xl font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl shadow-slate-300 dark:shadow-slate-900/50 active:scale-95 transition-all disabled:opacity-20"
                            >
                                <CartIcon className="w-4 h-4" /> 
                                {t('addToActive')}
                            </button>
                            <button 
                                onClick={handleCopyText} 
                                className="flex-1 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl font-black text-[11px] uppercase tracking-widest hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-sm"
                            >
                                {copyButtonText}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
};

export default BulkShoppingListModal;
